'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

interface Consent {
    functional: boolean;
    analytics: boolean;
}

interface CookieConsentContextType {
    visible: boolean;
    consent: Consent;
    showBanner: () => void;
    hideBanner: () => void;
    setConsent: (consent: Consent) => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
    const [visible, setVisible] = useState(false);
    const [consent, setConsentState] = useState<Consent>({ functional: true, analytics: false });

    // Читаем из localStorage при монтировании
    useEffect(() => {
        try {
            const saved = localStorage.getItem('cookie_consent');
            if (saved) {
                const parsed = JSON.parse(saved);
                setConsentState(parsed);  // подтягиваем сохранённый выбор
                setVisible(false);
                if (parsed.analytics) loadGoogleAnalytics();
            } else {
                setVisible(true);
            }
        } catch (error) {
            console.error('Error reading cookie consent from localStorage:', error);
            setVisible(true);
        }
    }, []);

    const loadGoogleAnalytics = () => {
        if (typeof window === 'undefined') return;
        const GA_ID = process.env.NEXT_PUBLIC_GA_ID || 'G-XXXXXXX';
        if (!GA_ID || GA_ID === 'G-XXXXXXX') return;

        if (!document.querySelector(`script[src="https://www.googletagmanager.com/gtag/js?id=${GA_ID}"]`)) {
            const script1 = document.createElement('script');
            script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
            script1.async = true;
            document.head.appendChild(script1);

            const script2 = document.createElement('script');
            script2.innerHTML = `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${GA_ID}', { anonymize_ip: true });
            `;
            document.head.appendChild(script2);
        }
    };

    // Обновление согласия
    const setConsent = (newConsent: Consent) => {
        try {
            setConsentState(newConsent); // обновляем state контекста
            localStorage.setItem('cookie_consent', JSON.stringify(newConsent)); // сохраняем
            setVisible(false);
            if (newConsent.analytics) loadGoogleAnalytics();
        } catch (error) {
            console.error('Error saving cookie consent:', error);
        }
    };

    const showBanner = () => setVisible(true);
    const hideBanner = () => setVisible(false);

    return (
        <CookieConsentContext.Provider
            value={{ visible, consent, setConsent, showBanner, hideBanner }}
        >
            {children}
        </CookieConsentContext.Provider>
    );
}

export function useCookieConsent() {
    const context = useContext(CookieConsentContext);
    if (!context) throw new Error('useCookieConsent must be used within CookieConsentProvider');
    return context;
}
